import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Stack,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  InputAdornment,
  Alert,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import RestoreIcon from "@mui/icons-material/Restore";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import api from "../../api";
import { adminHasPermission, adminHasAnyPermission } from "../../utils/adminPermissions";

const MASTER = "manage-roles-permissions";
// Top-level (portal) roles: they cannot have a base role. Matches RbacRoleController::BASE_ROLE_CONFIG_KEYS.
const TOP_LEVEL_ROLE_KEYS = ["super_admin", "admin", "parent", "student", "tutor", "tester"];
const DEFAULT_BASE_ROLES = [
  { value: "super_admin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "parent", label: "Parent" },
  { value: "student", label: "Student" },
  { value: "tutor", label: "Tutor" },
  { value: "tester", label: "Tester" },
];

const roleKey = (value) => String(value || "").toLowerCase().trim().replace(/\s+/g, "_");
const isTopLevelName = (name) => TOP_LEVEL_ROLE_KEYS.includes(roleKey(name));

const errorText = (err, fallback) => {
  const data = err?.response?.data;
  if (data?.errors) return Object.values(data.errors).flat().join(" ");
  return data?.message || fallback;
};

// Same portal scoping the old Master Forms role dialog used. No scope (standalone role) = all permissions.
const permPrefix = (p) => String(p?.name || "").toLowerCase();
const isParentPanel = (p) => permPrefix(p).startsWith("parent-panel-");
const isStudentPortal = (p) => permPrefix(p).startsWith("student-portal-");
const isTutorPortal = (p) => permPrefix(p).startsWith("tutor-portal-");
const scopePermissions = (list, scope) => {
  if (scope === "parent") return list.filter(isParentPanel);
  if (scope === "student") return list.filter(isStudentPortal);
  if (scope === "tutor") return list.filter(isTutorPortal);
  if (scope === "admin" || scope === "super_admin") {
    return list.filter((p) => !isParentPanel(p) && !isStudentPortal(p) && !isTutorPortal(p));
  }
  return list;
};

const groupPermissions = (list) => {
  const map = new Map();
  list.forEach((p) => {
    const g = p.group || "Other";
    if (!map.has(g)) map.set(g, []);
    map.get(g).push(p);
  });
  return Array.from(map.entries());
};

const EMPTY_FORM = { name: "", parent_role: "", description: "" };

export default function RolesManagement() {
  const canChange = adminHasPermission(MASTER);
  const canCreate = canChange && adminHasAnyPermission(["create-role", "manage-roles"]);
  const canUpdate = canChange && adminHasAnyPermission(["update-role", "manage-roles"]);
  const canToggle = canChange && adminHasAnyPermission(["toggle-role-status", "manage-roles"]);
  const canAssign = canChange && adminHasAnyPermission(["assign-role-permissions", "manage-roles"]);

  const [roles, setRoles] = useState([]);
  const [baseRoles, setBaseRoles] = useState(DEFAULT_BASE_ROLES);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Add / view / edit dialog
  const [dialog, setDialog] = useState(null); // { mode: 'create'|'view'|'edit', role }
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dialogLoading, setDialogLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permSearch, setPermSearch] = useState("");

  // Deactivate confirm
  const [confirm, setConfirm] = useState(null); // { role, usersCount }

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        api.get("admin/rbac/roles", { params: { summary: 1, with_inactive: 1 } }),
        api.get("admin/rbac/permissions"),
      ]);
      setRoles(Array.isArray(r.data?.data) ? r.data.data : []);
      if (Array.isArray(r.data?.base_roles) && r.data.base_roles.length > 0) setBaseRoles(r.data.base_roles);
      setPermissions(Array.isArray(p.data?.data) ? p.data.data : []);
    } catch (err) {
      toast.error(errorText(err, "Failed to load roles."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredRoles = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roles.filter((r) => {
      if (statusFilter === "active" && !r.is_active) return false;
      if (statusFilter === "inactive" && r.is_active) return false;
      if (q && !`${r.name} ${r.description || ""} ${r.parent_role || ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [roles, search, statusFilter]);

  // ----- dialog state -----
  const mode = dialog?.mode;
  const readOnly = mode === "view";
  // Top-level roles scope by their own name; sub-roles by their base; standalone roles see everything.
  const nameIsTopLevel = isTopLevelName(form.name);
  const scope = nameIsTopLevel ? roleKey(form.name) : roleKey(form.parent_role);
  const superAdminLike = scope === "super_admin";
  const permissionsEditable = !readOnly && canAssign;
  const detailsEditable = !readOnly && (mode === "create" ? canCreate : canUpdate);

  const scopedPermissions = useMemo(() => scopePermissions(permissions, scope), [permissions, scope]);

  const visibleGroups = useMemo(() => {
    const q = permSearch.trim().toLowerCase();
    // View mode shows exactly what the role has; edit/create shows the role's portal scope.
    const base = readOnly ? permissions.filter((p) => selectedIds.has(p.id)) : scopedPermissions;
    const list = q
      ? base.filter((p) => `${p.name} ${p.group || ""} ${p.description || ""}`.toLowerCase().includes(q))
      : base;
    return groupPermissions(list);
  }, [readOnly, permissions, scopedPermissions, selectedIds, permSearch]);

  const isLocked = (p) => !permissionsEditable || (p.super_admin_only && !superAdminLike);

  const openDialog = async (nextMode, role = null) => {
    setPermSearch("");
    setDialog({ mode: nextMode, role });
    if (!role) {
      setForm({ ...EMPTY_FORM });
      setSelectedIds(new Set());
      return;
    }
    setForm({ name: role.name, parent_role: role.parent_role || "", description: role.description || "" });
    setSelectedIds(new Set());
    setDialogLoading(true);
    try {
      const res = await api.get(`admin/rbac/roles/${role.id}`);
      setSelectedIds(new Set((res.data?.data?.permissions || []).map((p) => p.id)));
    } catch (err) {
      toast.error(errorText(err, "Failed to load role permissions."));
      setDialog(null);
    } finally {
      setDialogLoading(false);
    }
  };

  // Base role only narrows which permissions are listed; it does not pre-fill any.
  const changeBaseRole = (value) => setForm((f) => ({ ...f, parent_role: value }));

  const togglePermission = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroup = (items, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      items.filter((p) => !isLocked(p)).forEach((p) => (checked ? next.add(p.id) : next.delete(p.id)));
      return next;
    });
  };

  // Only permissions inside the role's portal scope are saved (the backend keeps attached inactive ones).
  const idsToSave = () => {
    const allowed = new Set(scopedPermissions.map((p) => p.id));
    return Array.from(selectedIds).filter((id) => allowed.has(id));
  };

  const saveDialog = async () => {
    const name = form.name.trim();
    if (detailsEditable) {
      if (!name) {
        toast.error("Role name is required.");
        return;
      }
      if (!nameIsTopLevel && form.parent_role && roleKey(form.parent_role) === roleKey(name)) {
        toast.error("A role cannot be its own base role.");
        return;
      }
    }
    setSaving(true);
    try {
      if (mode === "create") {
        const payload = { name, description: form.description.trim() || null };
        if (!nameIsTopLevel && form.parent_role) payload.parent_role = form.parent_role;
        if (canAssign) payload.permission_ids = idsToSave();
        await api.post("admin/rbac/roles", payload);
        toast.success("Role created.");
      } else {
        const role = dialog.role;
        if (detailsEditable) {
          const payload = { description: form.description.trim() || null };
          if (!role.is_core) payload.name = name;
          // Only send the base role when it changed, so existing roles (e.g. older data) save untouched.
          const originalBase = role.parent_role || "";
          if (!nameIsTopLevel && (form.parent_role || "") !== originalBase) {
            payload.parent_role = form.parent_role || null;
          }
          await api.put(`admin/rbac/roles/${role.id}`, payload);
        }
        if (permissionsEditable) {
          await api.put(`admin/rbac/roles/${role.id}/permissions`, { permission_ids: idsToSave() });
        }
        toast.success("Role updated.");
      }
      setDialog(null);
      load();
    } catch (err) {
      toast.error(errorText(err, "Failed to save role."));
    } finally {
      setSaving(false);
    }
  };

  // ----- status -----
  const setStatus = async (role, activate, force = false) => {
    try {
      await api.patch(`admin/rbac/roles/${role.id}/status`, { is_active: activate, force });
      toast.success(activate ? "Role activated." : "Role deactivated.");
      setConfirm(null);
      load();
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data?.requires_confirmation) {
        setConfirm({ role, usersCount: err.response.data.users_count });
        return;
      }
      toast.error(errorText(err, "Failed to change role status."));
      setConfirm(null);
    }
  };

  const dialogTitle =
    mode === "create" ? "Add Role" : mode === "edit" ? "Edit Role" : `View Role`;

  return (
    <Box sx={{ p: 3, minHeight: "100vh", background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)" }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
        Roles
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create roles, choose their base role, assign permissions and switch them on or off. An inactive role grants
        no permissions to the users who hold it.
      </Typography>
      {!canChange && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can view roles. Changes need the "{MASTER}" permission.
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }} justifyContent="space-between">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              size="small"
              placeholder="Search roles"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 260 }}
            />
            <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Refresh
            </Button>
            {canCreate && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog("create")}>
                Add New
              </Button>
            )}
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Role Name</TableCell>
                  <TableCell>Base Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Permissions</TableCell>
                  <TableCell align="right">Users</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoles.map((role) => (
                  <TableRow key={role.id} hover sx={{ opacity: role.is_active ? 1 : 0.6 }}>
                    <TableCell>
                      <Typography fontWeight={600}>
                        {role.name} {role.is_core ? <Chip size="small" label="Core" sx={{ ml: 1 }} /> : null}
                      </Typography>
                      {role.description ? (
                        <Typography variant="caption" color="text.secondary">{role.description}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {role.parent_role && !isTopLevelName(role.name)
                          ? baseRoles.find((b) => b.value === role.parent_role)?.label || role.parent_role
                          : isTopLevelName(role.name)
                            ? "Top-level"
                            : "None (standalone)"}
                      </Typography>
                      {role.available_portals ? (
                        <Typography variant="caption" color="text.secondary">{role.available_portals}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={role.is_active ? "success" : "default"} label={role.is_active ? "Active" : "Inactive"} />
                    </TableCell>
                    <TableCell align="right">
                      {role.active_permissions_count}
                      {role.permissions_count !== role.active_permissions_count ? (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {" "}(+{role.permissions_count - role.active_permissions_count} inactive)
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">{role.users_count}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => openDialog("view", role)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {(canUpdate || canAssign) && role.is_active && (
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary" onClick={() => openDialog("edit", role)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canToggle && !role.is_core && (
                        role.is_active ? (
                          <Tooltip title="Set Inactive">
                            <IconButton size="small" color="error" onClick={() => setConfirm({ role, usersCount: role.users_count })}>
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Set Active">
                            <IconButton size="small" color="success" onClick={() => setStatus(role, true)}>
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredRoles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No roles found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add / View / Edit role */}
      <Dialog open={!!dialog} onClose={() => !saving && setDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Role Name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={!detailsEditable || !!dialog?.role?.is_core}
              helperText={dialog?.role?.is_core && !readOnly ? "Core roles cannot be renamed." : ""}
              fullWidth
            />
            <TextField
              select
              label="Base Role (optional)"
              value={nameIsTopLevel ? "" : form.parent_role || ""}
              onChange={(e) => changeBaseRole(e.target.value)}
              disabled={!detailsEditable || nameIsTopLevel}
              helperText={
                nameIsTopLevel
                  ? "Top-level role: it has no base role."
                  : form.parent_role
                    ? "Sub-role: users sign in to the base role's portal; only that portal's permissions are listed."
                    : "Standalone: adds its permissions to users' other roles; all permissions are listed."
              }
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            >
              <MenuItem value="">{nameIsTopLevel ? "None (top-level role)" : "None (standalone role)"}</MenuItem>
              {baseRoles
                .filter((b) => b.value !== roleKey(form.name))
                .map((b) => (
                  <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
                ))}
              {form.parent_role && !nameIsTopLevel && !baseRoles.some((b) => b.value === form.parent_role) ? (
                <MenuItem value={form.parent_role} disabled>{form.parent_role} (not a valid base)</MenuItem>
              ) : null}
            </TextField>
          </Stack>
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            disabled={!detailsEditable}
            inputProps={{ maxLength: 255 }}
            fullWidth
            sx={{ mb: 2 }}
          />

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
              <Typography fontWeight={700}>
                Permissions{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                  ({readOnly ? selectedIds.size : idsToSave().length} selected)
                </Typography>
              </Typography>
              <TextField
                size="small"
                placeholder="Search permissions"
                value={permSearch}
                onChange={(e) => setPermSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
            </Stack>
            {!readOnly && !canAssign && (
              <Alert severity="info" sx={{ mb: 1 }}>You can't change permissions on roles.</Alert>
            )}
            <Divider sx={{ mb: 1 }} />
            {dialogLoading ? (
              <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : visibleGroups.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                {readOnly ? "This role has no permissions." : "No permissions match this base role (portal) scope."}
              </Typography>
            ) : (
              <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
                {visibleGroups.map(([group, items]) => {
                  const editable = items.filter((p) => !isLocked(p));
                  const checkedCount = items.filter((p) => selectedIds.has(p.id)).length;
                  const allEditableChecked = editable.length > 0 && editable.every((p) => selectedIds.has(p.id));
                  return (
                    <Box key={group} sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={readOnly ? true : allEditableChecked}
                            indeterminate={!readOnly && checkedCount > 0 && !allEditableChecked}
                            disabled={readOnly || editable.length === 0}
                            onChange={(e) => toggleGroup(items, e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="subtitle2" color="primary" sx={{ textTransform: "uppercase", fontWeight: 700 }}>
                            {group}{" "}
                            <Typography component="span" variant="caption" color="text.secondary">
                              ({checkedCount}/{items.length})
                            </Typography>
                            {items[0]?.super_admin_only ? <Chip size="small" label="Super Admin only" sx={{ ml: 1 }} /> : null}
                          </Typography>
                        }
                      />
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, pl: 3 }}>
                        {items.map((p) => (
                          <FormControlLabel
                            key={p.id}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedIds.has(p.id)}
                                disabled={isLocked(p)}
                                onChange={() => togglePermission(p.id)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">
                                  {p.name}
                                  {!p.is_active ? <Chip size="small" label="inactive" sx={{ ml: 0.5, height: 18 }} /> : null}
                                </Typography>
                                {p.description ? (
                                  <Typography variant="caption" color="text.secondary">{p.description}</Typography>
                                ) : null}
                              </Box>
                            }
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={saving}>{readOnly ? "Close" : "Cancel"}</Button>
          {readOnly && (canUpdate || canAssign) && dialog?.role?.is_active && (
            <Button variant="outlined" onClick={() => setDialog({ ...dialog, mode: "edit" })}>Edit</Button>
          )}
          {!readOnly && (detailsEditable || permissionsEditable) && (
            <Button variant="contained" onClick={saveDialog} disabled={saving || dialogLoading}>
              {saving ? "Saving..." : mode === "create" ? "Create" : "Update"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Deactivate confirm */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Set {confirm?.role?.name} inactive?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {confirm?.usersCount
              ? `${confirm.usersCount} user(s) have this role. They lose all of its permissions until it is set active again.`
              : "Nobody has this role right now. It can be set active again at any time."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => setStatus(confirm.role, false, true)}>
            Set Inactive
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
